import { FunctionComponent, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import ToasterWithMessage from "../../components/shared-components/toaster";
import { config } from "../../config/config";
import { GET_SUB_ADMINS, UPDATE_SUB_ADMIN } from "./api/AdminAccessQuery";
import styles from "./AdminAccess.module.css";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

type SubAdmin = {
  u_id: string;
  u_first_name: string;
  u_last_name: string;
  u_email_id: string;
  u_phone_number?: string;
  u_country_code?: string;
  u_is_active: boolean;
  u_delete_requested: boolean;
  u_created_at: string;
};

type CreateAdminForm = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  password: string;
  confirmPassword: string;
};

function formatPhoneForTable(raw?: string | null): string {
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length > 0) return digits;
  return "-";
}

const AdminAccess: FunctionComponent = () => {
  const navigate = useNavigate();
  const { error, success } = ToasterWithMessage();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<SubAdmin | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [form, setForm] = useState<CreateAdminForm>({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { data, loading, refetch } = useQuery<{ user: SubAdmin[] }>(GET_SUB_ADMINS, {
    fetchPolicy: "network-only",
    onError: () => error("Unable to load admins."),
  });

  const [updateSubAdmin] = useMutation(UPDATE_SUB_ADMIN);
  const subAdmins = useMemo(() => data?.user || [], [data]);

  const nodeApiBaseUrl = (process.env.REACT_APP_NODE_API_URL || config.node_api_url || "").replace(/\/+$/, "");
  const getAuthHeaders = () => {
    const idTokenKey = Object.keys(localStorage).find((key) => key.includes("idToken")) as string;
    const idToken = idTokenKey ? localStorage.getItem(idTokenKey) : "";
    const hasuraRole = localStorage.getItem("x-hasura-user-role") || "";
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (idToken) {
      headers.Authorization = `Bearer ${idToken}`;
    }
    if (hasuraRole) {
      headers["x-hasura-role"] = hasuraRole;
    }
    return headers;
  };

  const resetForm = () =>
    setForm({
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      password: "",
      confirmPassword: "",
    });

const createSubAdmin = async () => {
  // Validate all fields are filled
  if (!form.firstName || !form.lastName || !form.phoneNumber || !form.email || !form.password || !form.confirmPassword) {
    error("Please fill all required fields.");
    return;
  }
  
  // Validate phone number format
  if (!/^\d{10}$/.test(form.phoneNumber.trim())) {
    error("Phone number must be 10 digits.");
    return;
  }
  
  // Validate password match
  if (form.password !== form.confirmPassword) {
    error("Password and confirm password do not match.");
    return;
  }
  
  // Validate password length
  if (form.password.length < 10) {
    error("Password must be at least 10 characters.");
    return;
  }

  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(form.email.trim())) {
    error("Please enter a valid email address.");
    return;
  }

  // Check for duplicate email or phone number in existing admins
  const normalizedEmail = form.email.trim().toLowerCase();
  const normalizedPhone = form.phoneNumber.trim();
  
  const duplicateEmail = subAdmins.find(
    (admin) => admin.u_email_id.toLowerCase() === normalizedEmail
  );
  
  if (duplicateEmail) {
    error(`Admin with email "${normalizedEmail}" already exists.`);
    return;
  }

  // Check for duplicate phone number (only if phone number exists)
  const duplicatePhone = subAdmins.find(
    (admin) => admin.u_phone_number === normalizedPhone
  );
  
  if (duplicatePhone) {
    error(`Admin with phone number "${formatPhoneForTable(normalizedPhone)}" already exists.`);
    return;
  }

  if (!nodeApiBaseUrl) {
    error("Node API URL is not configured.");
    return;
  }
  
  setCreateLoading(true);
  try {
    const response = await fetch(`${nodeApiBaseUrl}/api/createStaffUser`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
        input: {
          createSubAdminUserInput: {
            adminEmailId: normalizedEmail,
            adminPassword: form.password,
            adminFirstName: form.firstName.trim(),
            adminLastName: form.lastName.trim(),
            adminPhoneNumber: normalizedPhone,
            adminCountryCode: "+1",
          },
        },
      }),
    });
    const responseData = await response.json().catch(() => ({}));
    if (!response.ok) {
      // Check for duplicate key violations from backend
      const errorMsg = responseData?.message || "Failed to create admin.";
      if (errorMsg.toLowerCase().includes("duplicate") || 
          errorMsg.toLowerCase().includes("unique constraint") ||
          errorMsg.toLowerCase().includes("already exists")) {
        if (errorMsg.toLowerCase().includes("email")) {
          error(`Admin with email "${normalizedEmail}" already exists.`);
        } else if (errorMsg.toLowerCase().includes("phone")) {
          error(`Admin with phone number "${formatPhoneForTable(normalizedPhone)}" already exists.`);
        } else {
          error("An admin with this email or phone number already exists.");
        }
      } else {
        error(errorMsg);
      }
      return;
    }
    success(responseData?.message || "Admin created successfully.");
    resetForm();
    setShowCreateModal(false);
    await refetch();
  } catch (err: any) {
    // Check for duplicate key violations in the error
    const errorMsg = err?.message || "Failed to create admin.";
    if (errorMsg.toLowerCase().includes("duplicate") || 
        errorMsg.toLowerCase().includes("unique constraint") ||
        errorMsg.toLowerCase().includes("already exists")) {
      if (errorMsg.toLowerCase().includes("email")) {
        error(`Admin with email "${normalizedEmail}" already exists.`);
      } else if (errorMsg.toLowerCase().includes("phone")) {
        error(`Admin with phone number "${formatPhoneForTable(normalizedPhone)}" already exists.`);
      } else {
        error("An admin with this email or phone number already exists.");
      }
    } else {
      error(errorMsg);
    }
  } finally {
    setCreateLoading(false);
  }
};

  const updateStatus = async (admin: SubAdmin, isActive: boolean) => {
    try {
      await updateSubAdmin({
        variables: {
          userId: admin.u_id,
          input: { u_is_active: isActive },
        },
      });
      success(`Admin ${isActive ? "activated" : "deactivated"} successfully.`);
      await refetch();
    } catch (_err: any) {
      error("Unable to update status.");
    }
  };

const saveEdit = async () => {
  if (!editingAdmin) return;
  const first = (editingAdmin.u_first_name || "").trim();
  const last = (editingAdmin.u_last_name || "").trim();
  const email = (editingAdmin.u_email_id || "").trim().toLowerCase();
  const phoneDigits = (editingAdmin.u_phone_number || "").replace(/\D/g, "");
  
  // Validate required fields
  if (!first || !last) {
    error("First name and last name are required.");
    return;
  }
  if (!email) {
    error("Email is required.");
    return;
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    error("Please enter a valid email address.");
    return;
  }
  if (phoneDigits.length > 0 && phoneDigits.length !== 10) {
    error("Phone number must be 10 digits (or leave blank).");
    return;
  }

  // ✅ FIXED: Check for duplicate email (excluding current admin)
  const duplicateEmail = subAdmins.find(
    (admin) => 
      admin.u_id !== editingAdmin.u_id && 
      admin.u_email_id.toLowerCase() === email
  );
  
  if (duplicateEmail) {
    error(`Admin with email "${email}" already exists.`);
    return;
  }

  // ✅ FIXED: Check for duplicate phone number (excluding current admin)
  if (phoneDigits.length === 10) {
    const duplicatePhone = subAdmins.find(
      (admin) => 
        admin.u_id !== editingAdmin.u_id && 
        admin.u_phone_number === phoneDigits
    );
    
    if (duplicatePhone) {
      error(`Admin with phone number "${formatPhoneForTable(phoneDigits)}" already exists.`);
      return;
    }
  }

  try {
    const input: {
      u_first_name: string;
      u_last_name: string;
      u_email_id: string;
      u_phone_number: string | null;
      u_country_code?: string;
    } = {
      u_first_name: first,
      u_last_name: last,
      u_email_id: email,
      u_phone_number: phoneDigits.length === 10 ? phoneDigits : null,
    };
    if (phoneDigits.length === 10) {
      input.u_country_code = (editingAdmin.u_country_code || "").trim() || "+1";
    }
    await updateSubAdmin({
      variables: {
        userId: editingAdmin.u_id,
        input,
      },
    });
    success("Admin updated successfully.");
    setEditingAdmin(null);
    await refetch();
  } catch (_err: any) {

    const errorMsg = _err?.message || "Unable to update admin.";
    if (errorMsg.toLowerCase().includes("duplicate") || 
        errorMsg.toLowerCase().includes("unique constraint") ||
        errorMsg.toLowerCase().includes("already exists")) {
      if (errorMsg.toLowerCase().includes("email")) {
        error(`Admin with email "${email}" already exists.`);
      } else if (errorMsg.toLowerCase().includes("phone")) {
        error(`Admin with phone number "${formatPhoneForTable(phoneDigits)}" already exists.`);
      } else {
        error("An admin with this email or phone number already exists.");
      }
    } else {
      error("Unable to update admin.");
    }
  }
};

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <div>
          <h2 className={styles.title}>Admin Access</h2>
          <p className={styles.subtitle}>Create and manage admins with role-based page permissions.</p>
          <div className={styles.topTabs}>
            <button className={styles.topTabButton} onClick={() => navigate("/role-page-access")}>
              Role Access
            </button>
            <button className={`${styles.topTabButton} ${styles.topTabButtonActive}`}>Admin Access</button>
          </div>
        </div>
        <button className={styles.addButton} onClick={() => setShowCreateModal(true)}>
          Add Admin
        </button>
      </div>

      <div className={styles.card}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(subAdmins || []).map((admin) => (
              <tr key={admin.u_id}>
                <td>{(admin.u_first_name || "").trim() || "-"}</td>
                <td>{(admin.u_last_name || "").trim() || "-"}</td>
                <td>{admin.u_email_id}</td>
                <td>{formatPhoneForTable(admin.u_phone_number)}</td>
                <td>{admin.u_is_active ? "Active" : "Deactivated"}</td>
                <td>{new Date(admin.u_created_at).toLocaleDateString()}</td>
                <td>
                  <div className={styles.actions}>
                    <button className={styles.actionBtn} onClick={() => setEditingAdmin({ ...admin })}>
                      Edit
                    </button>
                    <button
                      className={styles.actionBtn}
                      onClick={() => updateStatus(admin, !admin.u_is_active)}
                    >
                      {admin.u_is_active ? "Deactivate" : "Activate"}
                    </button>
                  </div>
                 </td>
               </tr>
            ))}
            {!loading && subAdmins.length === 0 && (
              <tr>
                <td colSpan={7}>No admins found.</td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={7}>Loading admins...</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ADD ADMIN MODAL */}
      {showCreateModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Add New Admin</h3>
            <form className={styles.formGrid} autoComplete="off">
              <label className={styles.field}>
                First Name
                <input
                  name="subAdminFirstName"
                  autoComplete="off"
                  value={form.firstName}
                  onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
                />
              </label>
              <label className={styles.field}>
                Last Name
                <input
                  name="subAdminLastName"
                  autoComplete="off"
                  value={form.lastName}
                  onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
                />
              </label>
              <label className={styles.field}>
                Phone Number
                <input
                  name="subAdminPhone"
                  autoComplete="off"
                  value={form.phoneNumber}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, phoneNumber: e.target.value.replace(/\D/g, "").slice(0, 10) }))
                  }
                  placeholder="Enter 10-digit phone number"
                />
              </label>
              <label className={styles.field}>
                Email
                <input
                  type="email"
                  name="subAdminEmail"
                  autoComplete="new-email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                />
              </label>

              {/* Password field with toggle (inline styles) */}
              <label className={styles.field}>
                Password
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="subAdminPassword"
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                    style={{ paddingRight: "40px", width: "100%" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: "absolute",
                      right: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      padding: 0,
                      color: "#666",
                    }}
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </button>
                </div>
              </label>

              {/* Confirm Password field with toggle */}
              <label className={styles.field}>
                Confirm Password
                <div style={{ position: "relative" }}>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="subAdminConfirmPassword"
                    autoComplete="new-password"
                    value={form.confirmPassword}
                    onChange={(e) => setForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                    style={{ paddingRight: "40px", width: "100%" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: "absolute",
                      right: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      padding: 0,
                      color: "#666",
                    }}
                  >
                    {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </button>
                </div>
              </label>
            </form>
            <div className={styles.footer}>
              <button
                className={styles.cancelBtn}
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button className={styles.saveBtn} onClick={createSubAdmin} disabled={createLoading}>
                {createLoading ? "Creating..." : "Add Admin"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT ADMIN MODAL */}
      {editingAdmin && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Edit Admin</h3>
            <div className={styles.formGrid}>
              <label className={styles.field}>
                First Name
                <input
                  autoComplete="off"
                  value={editingAdmin.u_first_name || ""}
                  onChange={(e) => setEditingAdmin((prev) => (prev ? { ...prev, u_first_name: e.target.value } : prev))}
                />
              </label>
              <label className={styles.field}>
                Last Name
                <input
                  autoComplete="off"
                  value={editingAdmin.u_last_name || ""}
                  onChange={(e) => setEditingAdmin((prev) => (prev ? { ...prev, u_last_name: e.target.value } : prev))}
                />
              </label>
              <label className={styles.field}>
                Email
                <input
                  type="email"
                  name="editSubAdminEmail"
                  autoComplete="off"
                  value={editingAdmin.u_email_id || ""}
                  onChange={(e) =>
                    setEditingAdmin((prev) => (prev ? { ...prev, u_email_id: e.target.value } : prev))
                  }
                />
              </label>
              <label className={styles.field}>
                Phone Number
                <input
                  name="editSubAdminPhone"
                  autoComplete="off"
                  value={editingAdmin.u_phone_number || ""}
                  onChange={(e) =>
                    setEditingAdmin((prev) =>
                      prev
                        ? {
                            ...prev,
                            u_phone_number: e.target.value.replace(/\D/g, "").slice(0, 10),
                          }
                        : prev,
                    )
                  }
                  placeholder="10-digit number (optional)"
                />
              </label>
            </div>
            <div className={styles.footer}>
              <button className={styles.cancelBtn} onClick={() => setEditingAdmin(null)}>
                Cancel
              </button>
              <button className={styles.saveBtn} onClick={saveEdit}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAccess;